import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataChangedTick } from "../useDataChanged";
import { AppShell } from "../components/AppShell";
import { MissionGraphFlow } from "../components/MissionGraphFlow";
import { OverviewTopbar, ProjectsRow } from "../components/OverviewTopbar";
import { ScanBar } from "../components/ScanBar";
import { StartScanCta, type ScanCtaError } from "../components/StartScanCta";
import { MetricCards } from "../components/MetricCards";
import { defaultIcons } from "../components/MetricCardIcons";
import { IconEye } from "../components/icons";
import { useAppState } from "../AppStateContext";
import { useScanElapsed } from "../components/useScanElapsed";
import {
  ApiError,
  api,
  getScanStages,
  isProjectVerified,
  normalizeApiText,
  normalizeScanStatus,
  progressPercent,
  type AttackVector,
  type Finding,
  type ScanEvent,
} from "../api";
import { NewProjectDialog } from "../components/NewProjectDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useI18n } from "../i18n";
import OnboardingPage from "./OnboardingPage";
import "./overview.css";

const DECORATIVE_ROW_LABELS = ["数据深渊", "迷失于界", "虚实之间", "数据深渊"];
const IDLE_STAGES = ["RECON", "MAP", "ANALYZE", "EXPLOIT", "CHAIN", "REPORT"].map(
  (name) => ({ name, pct: 0 }),
);

/** "Эндпоинты" count — there is no REST resource for it; the engine reports the
 * discovered attack surface only via events. Prefer classified routes
 * (`api_route.classify` / `browser.authenticated_crawl` → `payload.routes`),
 * else fall back to the live-target counts (`live_targets.derived` urls+hosts). */
function deriveEndpoints(events: ScanEvent[]): number | null {
  let routes: number | null = null;
  let reported = 0; // count of agent.route_reported events (mission pipeline)
  let roleRoutes: number | null = null; // sum of agent.role_session_done.routes
  let live: number | null = null;
  for (const e of events) {
    // agent.route_reported carries a null payload but each event = one route.
    if (e.event_type === "agent.route_reported") {
      reported += 1;
      continue;
    }
    const p = e.payload as Record<string, unknown> | undefined;
    if (!p) continue;
    if (
      (e.event_type === "api_route.classify" ||
        e.event_type === "browser.authenticated_crawl") &&
      typeof p.routes === "number"
    ) {
      routes = Math.max(routes ?? 0, p.routes);
    } else if (e.event_type === "agent.role_session_done" && typeof p.routes === "number") {
      roleRoutes = (roleRoutes ?? 0) + p.routes;
    } else if (e.event_type === "live_targets.derived") {
      const urls = typeof p.urls === "number" ? p.urls : 0;
      const hosts = typeof p.hosts === "number" ? p.hosts : 0;
      if (urls || hosts) live = Math.max(live ?? 0, urls + hosts);
    }
  }
  // Classic crawler pipelines emit a cumulative `routes` count; the mission
  // pipeline doesn't, so fall back to the per-route agent events (which is why
  // the card used to sit empty on mission scans).
  return routes ?? (reported || null) ?? roleRoutes ?? live;
}

export default function OverviewPage({
  initialStatus,
  initialChatOpen = false,
}: {
  initialStatus?: "running" | "paused";
  initialChatOpen?: boolean;
}) {
  const nav = useNavigate();
  const { t } = useI18n();
  const {
    projects,
    activeProject,
    setActiveProjectId,
    activeScan,
    activeScanGraph,
    viewedScan,
    pauseScan,
    resumeScan,
    cancelScan,
    startScan,
    reload,
    loading,
    initialized,
    deleteProject,
  } = useAppState();

  const dataTick = useDataChangedTick();
  const [chatOpen, setChatOpen] = useState(initialChatOpen);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [events, setEvents] = useState<ScanEvent[]>([]);
  const [vectors, setVectors] = useState<AttackVector[]>([]);
  const [endpointsCount, setEndpointsCount] = useState<number | null>(null);
  const [findingsLoaded, setFindingsLoaded] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [scanError, setScanError] = useState<ScanCtaError | null>(null);
  const [startingScan, setStartingScan] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  // Brief opacity dip on scan switch so changing numbers/bars don't visually
  // pop — the new data arrives during the dip and crossfades back in.
  const [swapping, setSwapping] = useState(false);
  const lastShownScanId = useRef<string | null>(null);
  const displayedScanError = useMemo<ScanCtaError | null>(() => {
    if (!scanError) return null;
    if (typeof scanError === "string") return scanError;
    if (!scanError.action) return scanError;
    return { ...scanError, action: { ...scanError.action, busy: resendBusy } };
  }, [scanError, resendBusy]);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; label: string } | null>(null);
  const displayScan = viewedScan ?? activeScan;
  const displayScanId = displayScan?.scan_id ?? null;
  const activeScanId = activeScan?.scan_id ?? null;
  const activeScanStatus = normalizeScanStatus(activeScan?.status);
  const showEmptyState =
    initialized && !projects.some(isProjectVerified);

  // Apply the initialStatus pseudo-deep-link once on mount.
  useEffect(() => {
    if (initialStatus === "paused" && activeScanId && activeScanStatus === "running") {
      void pauseScan();
    }
  }, [initialStatus, activeScanId, activeScanStatus, pauseScan]);

  useEffect(() => {
    if (lastShownScanId.current === displayScanId) return;
    if (lastShownScanId.current !== null && displayScanId !== null) {
      setSwapping(true);
      const t = window.setTimeout(() => setSwapping(false), 260);
      lastShownScanId.current = displayScanId;
      return () => window.clearTimeout(t);
    }
    lastShownScanId.current = displayScanId;
  }, [displayScanId]);

  useEffect(() => {
    let cancelled = false;
    setFindingsLoaded(false);
    if (!displayScanId) {
      const resetId = window.setTimeout(() => {
        if (!cancelled) {
          setFindings([]);
          setEvents([]);
          setVectors([]);
          setEndpointsCount(null);
        }
      }, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(resetId);
      };
    }
    const load = async () => {
      try {
        const [f, e, v, ep] = await Promise.all([
          api.scanFindings(displayScanId),
          api.scanEvents(displayScanId),
          api.listAttackVectors(displayScanId),
          api.scanEvents(displayScanId, {
            kinds:
              "api_route.classify,browser.authenticated_crawl,live_targets.derived,agent.route_reported,agent.role_session_done",
            page_size: 200,
          }),
        ]);
        if (!cancelled) {
          setFindings(f.findings);
          setEvents(e.events);
          setVectors(v.vectors);
          setEndpointsCount(deriveEndpoints(ep.events));
        }
      } catch {
        /* TODO(openapi): findings/events shapes confirmed once spec arrives */
      } finally {
        if (!cancelled) setFindingsLoaded(true);
      }
    };
    const initialId = window.setTimeout(() => void load(), 0);
    const id = window.setInterval(load, 10_000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialId);
      window.clearInterval(id);
    };
  }, [displayScanId, dataTick]);

  // Canonical truth = scan.findings_count from the backend; the local
  // `findings` array lags during scan switches and would otherwise briefly
  // flip the metrics into decorative mode (零/空) before the new fetch lands.
  const hasScanData = (displayScan?.findings_count ?? 0) > 0 || findings.length > 0;
  const scanStartedAt = displayScan?.started_at ?? displayScan?.created_at ?? null;
  const scanElapsedSec = scanStartedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(scanStartedAt).getTime()) / 1000))
    : 0;
  const isWarmingUp =
    Boolean(displayScan) &&
    (normalizeScanStatus(displayScan?.status) === "running" ||
      normalizeScanStatus(displayScan?.status) === "paused") &&
    scanElapsedSec < 20 * 60 &&
    findingsLoaded &&
    !hasScanData;

  const metrics = useMemo(() => {
    // The scan's own counters can lag behind the /findings feed mid-scan
    // (backend reports findings_count: 0 while the list already has rows), which
    // showed a "0" card above a non-empty "Recent findings" list. Reconcile by
    // taking whichever source is ahead.
    const localCritical = findings.filter(
      (f) => normalizeApiText(f.severity) === "critical",
    ).length;
    const localHigh = findings.filter((f) => normalizeApiText(f.severity) === "high").length;
    const findingsCount = Math.max(displayScan?.findings_count ?? 0, findings.length);
    const critCount = Math.max(displayScan?.critical_count ?? 0, localCritical);
    const highCount = Math.max(displayScan?.high_count ?? 0, localHigh);
    // Hypotheses = the agent's attack vectors (derived from the scan events
    // feed by api.listAttackVectors), NOT findings.
    // Map the vector lifecycle to the card's split: "в анализе" = still being
    // worked (pending/assigned/tested), "цепочек" = confirmed.
    const hypotesesCount = vectors.length;
    const reactiveCount = vectors.filter((v) => {
      const s = normalizeApiText(v.status);
      return s === "pending" || s === "assigned" || s === "tested";
    }).length;
    const chainCount = vectors.filter(
      (v) => normalizeApiText(v.status) === "confirmed",
    ).length;
    const crawledPct = progressPercent(displayScan?.progress_percent ?? 0);

    const decorative = !hasScanData;

    return [
      {
        key: "findings" as const,
        label: t("overview.metricFindings"),
        sub: decorative
          ? "数据深渊"
          : t("overview.metricFindingsSub", { critical: critCount, high: highCount }),
        value: decorative ? "零" : String(findingsCount),
        valueCjk: decorative,
        icon: defaultIcons.findings,
      },
      {
        key: "hypoteses" as const,
        label: t("overview.metricHypoteses"),
        sub: decorative
          ? "数据深渊"
          : t("overview.metricHypotesesSub", { reactive: reactiveCount, chain: chainCount }),
        value: decorative ? "空" : String(hypotesesCount),
        valueCjk: decorative,
        icon: defaultIcons.hypoteses,
      },
      {
        key: "endpoints" as const,
        label: t("overview.metricEndpoints"),
        sub: decorative ? "数据深渊" : t("overview.metricEndpointsSub", { percent: crawledPct }),
        value: decorative
          ? "零"
          : endpointsCount != null
            ? String(endpointsCount)
            : t("common.dash"),
        valueCjk: decorative,
        icon: defaultIcons.endpoints,
      },
    ];
  }, [findings, vectors, endpointsCount, displayScan, hasScanData, t]);

  const hasActive = !!displayScan;
  const elapsed = useScanElapsed(displayScan);
  const sortedFindings = useMemo(
    () => [...findings].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)).slice(0, 4),
    [findings],
  );
  const latestEvents = useMemo(() => events.slice(0, 4), [events]);

  const handleToggleScan = async () => {
    if (!activeScan) return;
    if (activeScanStatus === "running") await pauseScan();
    else await resumeScan();
  };

  const handleCancelScan = async () => {
    setConfirmCancel(false);
    await cancelScan();
    await reload();
  };

  if (showEmptyState) {
    return <OnboardingPage />;
  }

  const resendVerificationEmail = async () => {
    setResendBusy(true);
    try {
      const resp = await api.startEmailVerification();
      const cooldown = resp?.cooldown_remaining_seconds ?? 0;
      if (resp?.accepted === false && cooldown > 0) {
        setScanError({ text: t("scan.resendEmailCooldown", { seconds: cooldown }) });
      } else {
        setScanError({ text: t("scan.resendEmailSent") });
      }
    } catch {
      setScanError({
        text: t("scan.resendEmailFailed"),
        action: { label: t("scan.resendEmailLink"), onClick: resendVerificationEmail },
      });
    } finally {
      setResendBusy(false);
    }
  };

  const handleStartScan = async () => {
    // Bail immediately on a repeat click — `startingScan` flips the button to a
    // disabled "starting…" state synchronously so the user gets instant feedback
    // and can't queue up extra scans while the request is in flight.
    if (!activeProject || startingScan) return;
    setStartingScan(true);
    setScanError(null);
    try {
      const { targets } = await api.listScopeTargets(activeProject.project_id);
      const verified = targets.find((tgt) => normalizeApiText(tgt.verification_status) === "verified");
      if (!verified) {
        setScanError(t("scan.noVerifiedDomain"));
        return;
      }
      await startScan(verified.target_id);
    } catch (err) {
      if (err instanceof ApiError && isNoPlanError(err)) {
        nav("/settings");
        return;
      }
      if (isEmailNotVerifiedError(err)) {
        setScanError({
          text: t("scan.errEmailNotVerified"),
          action: { label: t("scan.resendEmailLink"), onClick: resendVerificationEmail },
        });
        return;
      }
      const msg = err instanceof Error ? err.message : t("projects.scanStartFailed");
      setScanError(humanizeScanError(msg, t, err));
    } finally {
      setStartingScan(false);
    }
  };

  return (
    <AppShell
      sidebarCollapsed={chatOpen}
      chatOpen={chatOpen}
      onToggleChat={() => setChatOpen((v) => !v)}
    >
      <div className={`overview anim-page${swapping ? " overview--swapping" : ""}`}>
        <OverviewTopbar />
        <ProjectsRow
          searches={projects.map((p) => ({
            id: p.project_id,
            label: p.name,
            verified: isProjectVerified(p),
          }))}
          activeId={activeProject?.project_id}
          onSelect={(id) => {
            setActiveProjectId(id);
            const proj = projects.find((p) => p.project_id === id);
            // Unverified projects can't yet show real scan data — send the
            // user to the onboarding flow to finish DNS verification.
            if (proj && !isProjectVerified(proj)) nav("/onboarding");
          }}
          onNew={() => setNewDialogOpen(true)}
          onDelete={(id, label) => setConfirmDelete({ id, label })}
        />

        {hasActive ? (
          <ScanBar
            elapsed={elapsed}
            status={displayScan!.status ?? "running"}
            stages={getScanStages(displayScan, activeScanGraph)}
            onToggle={handleToggleScan}
            onDelete={() => setConfirmCancel(true)}
          />
        ) : (
          <>
            <ScanBar
              elapsed="00:00:00"
              status="idle"
              stages={IDLE_STAGES}
              onToggle={startingScan ? undefined : handleStartScan}
            />
            <StartScanCta
              onStart={handleStartScan}
              disabled={loading || !activeProject}
              busy={startingScan}
              error={displayedScanError}
            />
          </>
        )}

        {isWarmingUp ? (
          <div className="overview-banner" role="status" aria-live="polite">
            <span>{t("scan.warmupBanner")}</span>
            <IconEye width={24} height={24} />
          </div>
        ) : null}

        <p className="overview__section-label">{t("overview.sectionLabel")}</p>
        <MetricCards metrics={metrics} />

        <div className="lists">
          <div className="card list-card" data-tour="recent-findings">
            <p className="list-card__title">{t("overview.recentFindings")}</p>
            <div className="list-card__body anim-stagger">
              {sortedFindings.length === 0 ? (
                <DecorativeRows withDot />
              ) : (
                sortedFindings.map((f, i) => (
                  <button
                    type="button"
                    className="row row--interactive"
                    data-tour={i === 0 ? "finding-row" : undefined}
                    key={f.finding_id}
                    onClick={() => nav("/findings")}
                    title={t("overview.viewAllFindings")}
                  >
                    <div className="row__left">
                      <span className={`row__dot row__dot--${severityDot(f.severity)}`} />
                      <span className="row__label">{f.title}</span>
                    </div>
                    <div className="row__right">
                      {f.poc ? <span className="row__poc">PoC</span> : null}
                      <span>{(f.score ?? 0).toFixed(1)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="card list-card" data-tour="live-feed">
            <p className="list-card__title">{t("overview.liveFeed")}</p>
            <div className="list-card__body anim-stagger">
              {latestEvents.length === 0 ? (
                <DecorativeRows />
              ) : (
                latestEvents.map((f) => (
                  <button
                    type="button"
                    className="row row--interactive"
                    key={f.event_id}
                    onClick={() => nav("/feed")}
                    title={t("overview.viewFullFeed")}
                  >
                    <div className="row__left">
                      <span className="row__time">{formatEventTime(f.timestamp)}</span>
                      <span className={`row__stage row__stage--${stageColor(eventTag(f))}`}>
                        {normalizeApiText(eventTag(f)).toUpperCase()}
                      </span>
                      <span className="row__label">{f.message}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <MissionGraphFlow scanId={displayScan?.scan_id ?? null} />
      </div>

      <NewProjectDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreated={async (project) => {
          setNewDialogOpen(false);
          setActiveProjectId(project.project_id);
          nav("/onboarding");
        }}
      />
      <ConfirmDialog
        open={confirmCancel}
        title={t("scan.confirmCancelTitle")}
        body={t("scan.confirmCancelBody")}
        confirmLabel={t("scan.confirmCancelLabel")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleCancelScan}
        onClose={() => setConfirmCancel(false)}
      />
      <ConfirmDialog
        open={confirmDelete !== null}
        title={t("projects.confirmDeleteTitle")}
        body={t("projects.confirmDeleteBody", { name: confirmDelete?.label ?? "" })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deleteProject(confirmDelete.id);
          } catch (err) {
            setScanError(err instanceof Error ? err.message : t("projects.deleteFailed"));
          } finally {
            setConfirmDelete(null);
          }
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </AppShell>
  );
}

function isNoPlanError(err: ApiError): boolean {
  return err.status === 412 || err.code === "FAILED_PRECONDITION";
}

function isEmailNotVerifiedError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.code === "EMAIL_NOT_VERIFIED") return true;
  const msg = err.message?.toLowerCase() ?? "";
  return msg.includes("email") && msg.includes("verif");
}

function humanizeScanError(
  msg: string,
  t: (key: import("../i18n").TranslationKey, vars?: Record<string, string | number>) => string,
  err?: unknown,
): string {
  if (err instanceof ApiError && isNoPlanError(err)) {
    return t("scan.errNoPlan");
  }
  const lower = msg.toLowerCase();
  if (lower.includes("scan type") && lower.includes("not allowed")) {
    return t("scan.errTierBlocked");
  }
  if (lower.includes("billing") || lower.includes("limit")) {
    return t("scan.errBilling");
  }
  return msg;
}

function DecorativeRows({ withDot = false }: { withDot?: boolean }) {
  return (
    <>
      {DECORATIVE_ROW_LABELS.map((label, i) => (
        <div className="row row--decorative" key={i}>
          <div className="row__left">
            {withDot ? <span className="row__dot row__dot--placeholder" /> : null}
            <span className="row__label">{label}</span>
          </div>
          <span className="row__decoration-tail">空</span>
        </div>
      ))}
    </>
  );
}

function severityDot(sev: Finding["severity"]): string {
  switch (normalizeApiText(sev)) {
    case "critical":
      return "red";
    case "high":
      return "orange";
    case "medium":
      return "blue";
    case "low":
    default:
      return "purple";
  }
}

function eventTag(e: ScanEvent): string {
  return e.phase ?? e.node_type ?? e.event_type ?? "";
}

function stageColor(stage: string): string {
  switch (normalizeApiText(stage)) {
    case "exploit":
      return "red";
    case "rbc":
      return "orange";
    case "scanner":
      return "blue";
    case "recon":
      return "purple";
    default:
      return "blue";
  }
}

function formatEventTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return ts;
  }
}
